import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Chips, ChipsChangeParams } from "primereact/chips";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { IBenefitSelectionRule } from "types/Account/Account";

import { normaliseBenefitNameSynonyms } from "../../helpers/normaliseBenefitNameSynonyms";

const StyledMessage = styled(Message)`
  justify-content: flex-start;
`;

export const tierOptions = [
  { name: "Not set — use the payer's default tier", value: "" },
  { name: "Tier 1 (preferred)", value: "Tier1" },
  { name: "Tier 2", value: "Tier2" },
];

export const networkStatusOptions = [
  { name: "In network", value: "InNetwork" },
  { name: "Out of network", value: "OutOfNetwork" },
];

export const emptyRule = (): IBenefitSelectionRule => ({
  payerIds: [],
  planKeywords: [],
  matchKeywords: [],
  excludeKeywords: [],
  tierPreference: null,
  networkStatus: "InNetwork",
});

interface IBenefitSelectionRuleDialogProps {
  visible: boolean;
  /** The rule being edited, or a blank one when adding. */
  value: IBenefitSelectionRule;
  payerOptions: Array<{ label: string; value: number }>;
  loadingPayers: boolean;
  isNew: boolean;
  onSave: (rule: IBenefitSelectionRule) => void;
  onHide: () => void;
}

/**
 * CLE-2683 — one benefit rule, edited in isolation.
 *
 * The six controls used to sit inline, one bordered card per rule, which stopped scaling at about
 * three: accounts are heading for a rule per payer. The list is a table now and the editing happens
 * here, matching how the contract adjustment rules are managed.
 *
 * Edits are held locally and only handed back on save, so cancelling genuinely discards them
 * instead of leaving a half-edited rule in the form.
 */
export function BenefitSelectionRuleDialog({
  visible,
  value,
  payerOptions,
  loadingPayers,
  isNew,
  onSave,
  onHide,
}: IBenefitSelectionRuleDialogProps) {
  const [draft, setDraft] = useState<IBenefitSelectionRule>(value);

  useEffect(() => {
    if (visible) setDraft(value);
  }, [visible, value]);

  const set = (patch: Partial<IBenefitSelectionRule>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const chipField = (
    key: "planKeywords" | "matchKeywords" | "excludeKeywords",
    label: string,
    placeholder: string
  ) => (
    <div className="field col-12">
      <Label htmlFor={`benefitSelectionRuleDialog.${key}`}>{label}</Label>
      <Chips
        id={`benefitSelectionRuleDialog.${key}`}
        data-testid={`benefitSelectionRuleDialog.${key}`}
        className="w-full"
        value={draft[key] ?? []}
        placeholder={placeholder}
        // Normalised with the same rule the API applies, so a keyword that will not survive the
        // save is not left on screen looking saved.
        onChange={(e: ChipsChangeParams) =>
          set({ [key]: normaliseBenefitNameSynonyms(e.value ?? []) } as any)
        }
      />
    </div>
  );

  return (
    <Dialog
      header={isNew ? "Add benefit rule" : "Edit benefit rule"}
      visible={visible}
      onHide={onHide}
      style={{ width: "min(46rem, 95vw)" }}
      data-testid="benefitSelectionRuleDialog"
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            type="button"
            label="Cancel"
            className="p-button-text"
            data-testid="benefitSelectionRuleDialog.cancel"
            onClick={onHide}
          />
          <Button
            type="button"
            label="Save rule"
            data-testid="benefitSelectionRuleDialog.save"
            onClick={() => onSave(draft)}
          />
        </div>
      }
    >
      <div className="formgrid grid">
        <div className="field col-12 md:col-6">
          <Label htmlFor="benefitSelectionRuleDialog.payerIds">Payers</Label>
          <MultiSelect
            id="benefitSelectionRuleDialog.payerIds"
            data-testid="benefitSelectionRuleDialog.payerIds"
            className="w-full"
            display="chip"
            filter
            options={payerOptions}
            disabled={loadingPayers}
            value={draft.payerIds ?? []}
            placeholder="All payers"
            onChange={(e) => set({ payerIds: e.value ?? [] })}
          />
        </div>

        <div className="field col-12 md:col-6">
          <Label htmlFor="benefitSelectionRuleDialog.planKeywords">
            Plans / products (optional)
          </Label>
          <Chips
            id="benefitSelectionRuleDialog.planKeywords"
            data-testid="benefitSelectionRuleDialog.planKeywords"
            className="w-full"
            value={draft.planKeywords ?? []}
            placeholder="CHOICE PLUS"
            onChange={(e: ChipsChangeParams) =>
              set({ planKeywords: normaliseBenefitNameSynonyms(e.value ?? []) })
            }
          />
        </div>

        {chipField("matchKeywords", "Use lines containing", "FLEX PROVIDER")}
        {chipField(
          "excludeKeywords",
          "Never use lines containing",
          "OTHER PLAN PROVIDER"
        )}

        <div className="field col-12 md:col-6">
          <Label htmlFor="benefitSelectionRuleDialog.tierPreference">
            Tier
          </Label>
          <Dropdown
            id="benefitSelectionRuleDialog.tierPreference"
            data-testid="benefitSelectionRuleDialog.tierPreference"
            className="w-full"
            optionLabel="name"
            options={tierOptions}
            value={draft.tierPreference ?? ""}
            onChange={(e) =>
              set({ tierPreference: e.value === "" ? null : e.value })
            }
          />
        </div>

        <div className="field col-12 md:col-6">
          <Label htmlFor="benefitSelectionRuleDialog.networkStatus">
            Network status
          </Label>
          <Dropdown
            id="benefitSelectionRuleDialog.networkStatus"
            data-testid="benefitSelectionRuleDialog.networkStatus"
            className="w-full"
            optionLabel="name"
            options={networkStatusOptions}
            value={draft.networkStatus ?? "InNetwork"}
            onChange={(e) => set({ networkStatus: e.value })}
          />
        </div>

        <div className="col-12">
          <StyledMessage
            severity="info"
            text="Keywords match the wording on each benefit line, ignoring case, anywhere in the text — use the distinctive part (FLEX PROVIDER, not NETWORK). A keyword that matches nothing falls back to normal selection; an excluded keyword that rules out every line means the benefit is not quoted at all."
          />
        </div>
      </div>
    </Dialog>
  );
}
